import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  Edit,
  ChevronRight,
  Loader2,
  Info
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TimePicker } from '@/components/ui/time-picker';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Helper to format date
const formatDateDisplay = (date) => {
  return new Date(date).toLocaleDateString('id-ID', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

export default function NakesSchedulePopup({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dailyStatus, setDailyStatus] = useState(null);
  const [showTimeEdit, setShowTimeEdit] = useState(false);
  const [newSchedule, setNewSchedule] = useState({
    start_time: '09:00',
    end_time: '17:00'
  });

  useEffect(() => {
    checkDailyStatus();
  }, []);

  const checkDailyStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/nakes/schedule/daily-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDailyStatus(response.data);
      
      // If already checked today, close popup
      if (!response.data.show_popup) {
        onClose();
        return;
      }
      
      // Pre-fill schedule times if available
      if (response.data.today_schedule) {
        setNewSchedule({
          start_time: response.data.today_schedule.start_time || '09:00',
          end_time: response.data.today_schedule.end_time || '17:00'
        });
      }
    } catch (error) {
      console.error('Error checking daily status:', error);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action, schedule = null) => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const payload = { action };
      if (schedule) {
        payload.schedule = schedule;
      }
      
      const response = await axios.post(`${API}/nakes/schedule/daily-check`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      onClose();
    } catch (error) {
      console.error('Error submitting daily check:', error);
      toast.error('Gagal menyimpan');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl p-6 max-w-md w-full">
          <div className="flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        </div>
      </div>
    );
  }

  if (!dailyStatus?.show_popup) {
    return null;
  }

  const hasScheduleToday = dailyStatus.has_schedule_today;
  const todaySchedule = dailyStatus.today_schedule;
  const todayDate = new Date();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-xl">
        {/* Header */}
        <div className="p-6 bg-gradient-to-r from-blue-500 to-blue-600 text-white">
          <div className="flex items-center gap-3">
            <Calendar className="w-8 h-8" />
            <div>
              <h2 className="text-xl font-bold">Selamat Datang!</h2>
              <p className="text-blue-100 text-sm">
                {formatDateDisplay(todayDate)}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {hasScheduleToday ? (
            // Has schedule today - confirm/update/disable
            <>
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-green-800">Jadwal Hari Ini</span>
                </div>
                <div className="flex items-center gap-2 text-green-700">
                  <Clock className="w-4 h-4" />
                  <span>
                    {todaySchedule?.start_time || '09:00'} - {todaySchedule?.end_time || '17:00'}
                  </span>
                </div>
              </div>

              <p className="text-gray-600 mb-4">
                Apakah jadwal hari ini masih sesuai?
              </p>

              {showTimeEdit ? (
                <div className="mb-4 p-4 bg-gray-50 rounded-xl">
                  <p className="text-sm font-medium text-gray-700 mb-3">Ubah Jadwal Hari Ini</p>
                  <div className="flex items-center gap-2">
                    <TimePicker
                      value={newSchedule.start_time}
                      onChange={(val) => setNewSchedule(prev => ({ ...prev, start_time: val }))}
                      className="flex-1"
                      data-testid="popup-edit-start-time"
                    />
                    <span className="text-gray-400">-</span>
                    <TimePicker
                      value={newSchedule.end_time}
                      onChange={(val) => setNewSchedule(prev => ({ ...prev, end_time: val }))}
                      className="flex-1"
                      data-testid="popup-edit-end-time"
                    />
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button
                      onClick={() => handleAction('update', newSchedule)}
                      disabled={submitting}
                      className="flex-1"
                    >
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Simpan'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowTimeEdit(false)}
                      disabled={submitting}
                    >
                      Batal
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Button
                    onClick={() => handleAction('confirm')}
                    disabled={submitting}
                    className="w-full justify-between"
                  >
                    <span className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Sesuai
                    </span>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={() => setShowTimeEdit(true)}
                    disabled={submitting}
                    className="w-full justify-between"
                  >
                    <span className="flex items-center gap-2">
                      <Edit className="w-4 h-4" />
                      Ubah Jadwal
                    </span>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={() => handleAction('disable')}
                    disabled={submitting}
                    className="w-full justify-between text-orange-600 border-orange-200 hover:bg-orange-50"
                  >
                    <span className="flex items-center gap-2">
                      <XCircle className="w-4 h-4" />
                      Matikan Jadwal
                    </span>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </>
          ) : (
            // No schedule today - optional to set
            <>
              <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-5 h-5 text-gray-500" />
                  <span className="font-medium text-gray-700">Belum Ada Jadwal</span>
                </div>
                <p className="text-sm text-gray-500">
                  Anda belum mengisi jadwal untuk hari ini ({formatDateDisplay(todayDate)})
                </p>
              </div>

              <p className="text-gray-600 mb-4">
                Mau isi jadwal hari ini? (opsional)
              </p>

              {showTimeEdit ? (
                <div className="mb-4 p-4 bg-blue-50 rounded-xl">
                  <p className="text-sm font-medium text-gray-700 mb-3">Isi Jadwal Hari Ini</p>
                  <div className="flex items-center gap-2">
                    <TimePicker
                      value={newSchedule.start_time}
                      onChange={(val) => setNewSchedule(prev => ({ ...prev, start_time: val }))}
                      className="flex-1"
                      data-testid="popup-edit-start-time"
                    />
                    <span className="text-gray-400">-</span>
                    <TimePicker
                      value={newSchedule.end_time}
                      onChange={(val) => setNewSchedule(prev => ({ ...prev, end_time: val }))}
                      className="flex-1"
                      data-testid="popup-edit-end-time"
                    />
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button
                      onClick={() => handleAction('update', newSchedule)}
                      disabled={submitting}
                      className="flex-1"
                    >
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Simpan Jadwal'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowTimeEdit(false)}
                      disabled={submitting}
                    >
                      Batal
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Button
                    onClick={() => setShowTimeEdit(true)}
                    disabled={submitting}
                    className="w-full justify-between"
                  >
                    <span className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Isi Jadwal
                    </span>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={() => handleAction('skip')}
                    disabled={submitting}
                    className="w-full justify-between"
                  >
                    <span className="flex items-center gap-2">
                      <ChevronRight className="w-4 h-4" />
                      Lewati
                    </span>
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Info */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <div className="flex gap-2">
              <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-blue-700">
                Mengisi jadwal bersifat opsional. Nakes yang mengisi jadwal akan diprioritaskan oleh admin saat assignment job.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
